// The Theme object and its resolver. See docs/design/THEMING.md and
// VISUAL_IDENTITY.md §1. Pure: no DOM, no sport import; team palettes come
// in through `lookupTeam`.

import { z } from 'zod';
import { contrastRatio, deriveScale, mix, onColor } from './color';
import { MARK_TOKENS, type LookGrounds } from './look';

export const PRESET_IDS = [
  'default',
  'midnight',
  'turf',
  'chalkboard',
  'broadcast',
  'daylight',
  'high-contrast',
] as const;
export type PresetId = (typeof PRESET_IDS)[number];

const Hex = z.string().regex(/^#[0-9a-f]{6}$/i, 'Use #rrggbb');

export const ThemeSchema = z.object({
  mode: z.enum(['dark', 'light', 'system']).default('dark'),
  accent: z
    .discriminatedUnion('kind', [
      z.object({ kind: z.literal('team'), sport: z.string().min(1), teamId: z.string().min(1) }),
      z.object({ kind: z.literal('custom'), seed: Hex, secondary: Hex.optional() }),
      z.object({ kind: z.literal('preset'), id: z.enum(PRESET_IDS) }),
    ])
    .default({ kind: 'preset', id: 'default' }),
  fontScale: z.union([z.literal(0.9), z.literal(1), z.literal(1.1), z.literal(1.2)]).default(1),
  sounds: z.boolean().default(true),
});
export type Theme = z.infer<typeof Theme>;
export const Theme = ThemeSchema;

export const DEFAULT_THEME: Theme = ThemeSchema.parse({});

/** The same four-field shape as the Puzzle Site's theme registry, so its tile picker works here. */
export interface ThemeSwatch {
  id: string;
  label: string;
  body: string;
  surface: string;
  accent: string;
  mode: 'dark' | 'light';
}

export interface ThemePreset {
  id: PresetId;
  label: string;
  mode: 'dark' | 'light';
  seed: string;
  secondary: string;
  /** Optional canvas override for presets with a distinctive ground (chalkboard). */
  canvas?: { dark?: string; light?: string };
}

export const THEME_PRESETS: readonly ThemePreset[] = [
  { id: 'default', label: 'Default', mode: 'dark', seed: '#ff6b3d', secondary: '#ffd166' },
  { id: 'midnight', label: 'Midnight', mode: 'dark', seed: '#5aa9ff', secondary: '#b78cff' },
  { id: 'turf', label: 'Turf', mode: 'dark', seed: '#3ddc84', secondary: '#ffd166' },
  {
    id: 'chalkboard',
    label: 'Chalkboard',
    mode: 'dark',
    seed: '#f3f2ee',
    secondary: '#ffd166',
    canvas: { dark: '#1d2b24' },
  },
  { id: 'broadcast', label: 'Broadcast', mode: 'dark', seed: '#ffc62f', secondary: '#ff3b5c' },
  { id: 'daylight', label: 'Daylight', mode: 'light', seed: '#4f2683', secondary: '#ffc62f' },
  {
    id: 'high-contrast',
    label: 'High contrast',
    mode: 'dark',
    seed: '#ffff00',
    secondary: '#ffffff',
  },
];

const PRESET_BY_ID = new Map(THEME_PRESETS.map((p) => [p.id, p]));

// ── Neutral layers (VISUAL_IDENTITY.md §1.1) ─────────────────────────────

const DARK = {
  'bg-canvas': '#121317',
  'bg-surface': '#1b1d24',
  'bg-raised': '#23262f',
  'bg-sunken': '#0d0e11',
  'border-subtle': '#2a2d37',
  'border-strong': '#3a3e4b',
  'ink-primary': '#f3f2ee',
  'ink-secondary': '#a9abb5',
  'ink-muted': '#6f7280',
  win: '#3ddc84',
  loss: '#ff5d6c',
  warn: '#ffb547',
  live: '#ff3b5c',
  info: '#5aa9ff',
  'pos-qb': '#f0647c',
  'pos-rb': '#4cd3a5',
  'pos-wr': '#5aa9ff',
  'pos-te': '#ffb547',
  'pos-k': '#b78cff',
  'pos-dst': '#9aa3b2',
  // Defensive players (Q11): brick, olive and teal, apart from the offense's.
  'pos-dl': '#ee7d5f',
  'pos-lb': '#a8c95a',
  'pos-db': '#4fc6d6',
} as const;

const LIGHT = {
  'bg-canvas': '#f6f5f2',
  'bg-surface': '#ffffff',
  'bg-raised': '#ffffff',
  'bg-sunken': '#ece9e3',
  'border-subtle': '#e4e0d8',
  'border-strong': '#c9c4b8',
  'ink-primary': '#17181c',
  'ink-secondary': '#5c5f6a',
  'ink-muted': '#9195a1',
  win: '#1f9d5a',
  loss: '#d13848',
  warn: '#c77d00',
  live: '#e0284a',
  info: '#2b6fd6',
  'pos-qb': '#d13858',
  'pos-rb': '#1e9f75',
  'pos-wr': '#2b6fd6',
  'pos-te': '#c77d00',
  'pos-k': '#7b4fd1',
  'pos-dst': '#5c6675',
  'pos-dl': '#b4462b',
  'pos-lb': '#5b7a1c',
  'pos-db': '#14798a',
} as const;

export type TokenName =
  keyof typeof DARK | 'accent' | 'accent-on' | 'accent-text' | 'accent-secondary' | 'accent-soft';

export interface TeamPaletteLike {
  primary: string;
  secondary: string;
  accent?: string;
}

export interface ResolveOptions {
  /** `prefers-color-scheme: dark` at resolve time, for mode 'system'. */
  systemDark: boolean;
  /** Team palettes come from the sport packages; the theme package never imports one. */
  lookupTeam: (sport: string, teamId: string) => TeamPaletteLike | undefined;
  /**
   * The look's ground, when it lays one (a look folder's `grounds`): Crayon's
   * paper or chalkboard in place of the palette's surfaces and ink. Absent or
   * null is the palette's own (Clean).
   */
  grounds?: LookGrounds;
}

export interface ResolvedTheme {
  mode: 'dark' | 'light';
  /** Every token from VISUAL_IDENTITY.md §1.1 as hex. */
  tokens: Record<TokenName, string>;
  /** Ten steps, light to dark, for Mantine's color scale. */
  accentScale: readonly string[];
  /** True when the accent failed contrast for text and `accent-text` fell back to the secondary. */
  accentTextFellBack: boolean;
  /**
   * Which color the accent's words start from: the team's primary, its
   * secondary, or plain ink. `accent-text` is then lifted until it reads
   * (4.5:1), so it is that color's hue, not always that exact color.
   */
  accentTextFrom: 'primary' | 'secondary' | 'ink';
  swatch: ThemeSwatch;
  fontScale: Theme['fontScale'];
  sounds: boolean;
}

const MIN_TEXT_CONTRAST = 3;

const MIN_WORD_CONTRAST = 4.5;

/** The color, or the least step toward white (dark) or black (light) that reads on `on`. */
function readableOn(hex: string, on: string, mode: 'dark' | 'light'): string {
  const toward = mode === 'dark' ? '#ffffff' : '#000000';
  for (let step = 0; step <= 20; step++) {
    const candidate = step === 0 ? hex : mix(hex, toward, step * 0.05);
    if (contrastRatio(candidate, on) >= MIN_WORD_CONTRAST) return candidate;
  }
  return toward;
}

/** Hex with alpha, for the soft accent wash. */
function withAlpha(hex: string, alpha: number): string {
  const a = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex.toLowerCase()}${a}`;
}

function complement(seed: string): string {
  // A cheap complementary tone at similar lightness: invert hue by mixing
  // the channel-inverted color back toward the seed's lightness.
  const n = parseInt(seed.slice(1), 16);
  const inv = `#${(0xffffff - n).toString(16).padStart(6, '0')}`;
  return mix(inv, seed, 0.25);
}

export function resolveTheme(input: Theme, opts: ResolveOptions): ResolvedTheme {
  const theme = ThemeSchema.parse(input);

  // 1. Accent source → primary, secondary, preferred mode, label.
  let primary: string;
  let secondary: string;
  let label: string;
  let presetMode: 'dark' | 'light' | null = null;
  let canvasOverride: string | undefined;

  if (theme.accent.kind === 'team') {
    const team = opts.lookupTeam(theme.accent.sport, theme.accent.teamId);
    if (!team) throw new Error(`Unknown team ${theme.accent.sport}:${theme.accent.teamId}`);
    primary = team.primary;
    secondary = team.secondary;
    label = theme.accent.teamId;
  } else if (theme.accent.kind === 'custom') {
    primary = theme.accent.seed;
    secondary = theme.accent.secondary ?? complement(theme.accent.seed);
    label = 'Custom';
  } else {
    const preset = PRESET_BY_ID.get(theme.accent.id)!;
    primary = preset.seed;
    secondary = preset.secondary;
    label = preset.label;
    presetMode = preset.mode;
  }

  // 2. Mode. A preset carries its own mode when the user hasn't chosen one explicitly ('system').
  const mode: 'dark' | 'light' =
    theme.mode === 'system' ? (presetMode ?? (opts.systemDark ? 'dark' : 'light')) : theme.mode;
  // The look's ground, when it lays one, replaces the neutral layers and any
  // marks it draws in its own colors; the accent and a team's colors stay the
  // palette's.
  const ground = opts.grounds?.[mode] ?? null;
  const base = { ...(mode === 'dark' ? DARK : LIGHT), ...ground };
  // A preset's own canvas (Chalkboard's green) is a ground for a look that has
  // none; a look that lays its own keeps it.
  if (theme.accent.kind === 'preset' && ground === null) {
    const preset = PRESET_BY_ID.get(theme.accent.id)!;
    canvasOverride = mode === 'dark' ? preset.canvas?.dark : preset.canvas?.light;
  }

  // 3. Surfaces: tint 4% toward the primary in dark mode so the app feels like
  //    the team without becoming a jersey. Light mode stays clean, and so does
  //    a look's own ground: a chalkboard mixed toward an orange accent turned
  //    olive.
  const tint = (hex: string) =>
    mode === 'dark' && ground === null ? mix(hex, primary, 0.04) : hex;
  const surface = tint(base['bg-surface']);

  // 4. Contrast guard for text uses of the accent. The hue is the team's
  //    primary when it reads at all (3:1), else its secondary, else ink; then
  //    it is lifted until it reads as WORDS (4.5:1, WCAG 1.4.3) on the accent's
  //    own soft wash, the darkest place it is set: an active tab's label, a
  //    move's hint (UX_STANDARDS UX-27). At 3:1 a teal tab label read 3.8:1.
  const primaryOk = contrastRatio(primary, surface) >= MIN_TEXT_CONTRAST;
  const secondaryOk = contrastRatio(secondary, surface) >= MIN_TEXT_CONTRAST;
  const softAlpha = mode === 'dark' ? 0.16 : 0.12;
  const accentText = readableOn(
    readableOn(
      readableOn(
        primaryOk ? primary : secondaryOk ? secondary : base['ink-primary'],
        surface,
        mode,
      ),
      mix(surface, primary, softAlpha),
      mode,
    ),
    // And on the wash over the canvas, where the rail's chosen item sits: on
    // the canvas a teal label read 4.48:1 in light mode (the UX audit's run 7).
    mix(canvasOverride ?? tint(base['bg-canvas']), primary, softAlpha),
    mode,
  );

  // On a look's own ground, the palette's result and position colors keep
  // their hue and are lifted until they read as words on its surface (4.5:1),
  // the way the accent's text falls back above. Clean's ground is the one
  // those colors were chosen on, so it is left exactly as it was.
  const semantic: Partial<Record<keyof typeof DARK, string>> = {};
  // Against the surface, the canvas (a look may lay rows straight on its
  // canvas: Crayon's lists sit on the paper) and the accent's own wash, which
  // is the manager's own row: a loss or a live score there read 3.8:1 in
  // Clean too (UX_STANDARDS UX-27, B-277). A color that already reads on all
  // three is left exactly as it was.
  const canvas = canvasOverride ?? tint(base['bg-canvas']);
  const wash = mix(surface, primary, mode === 'dark' ? 0.16 : 0.12);
  for (const key of MARK_TOKENS) {
    semantic[key] = readableOn(
      readableOn(readableOn(base[key], surface, mode), canvas, mode),
      wash,
      mode,
    );
  }

  // MUTED INK IS STILL WORDS (UX_STANDARDS UX-27, B-277): a table's header, a
  // "vs", a quiet note. It keeps its step below the secondary and is lifted
  // only as far as reading at 4.5:1 on the surface and the canvas needs; it
  // read 3.4:1 in the dark and 3.0:1 in the light.
  const canvasHere = canvasOverride ?? tint(base['bg-canvas']);
  semantic['ink-muted'] = readableOn(
    readableOn(semantic['ink-muted'] ?? base['ink-muted'], surface, mode),
    canvasHere,
    mode,
  );

  const scale = deriveScale(primary);
  const tokens: Record<TokenName, string> = {
    ...base,
    ...semantic,
    'bg-canvas': canvasOverride ?? tint(base['bg-canvas']),
    'bg-surface': surface,
    'bg-raised': tint(base['bg-raised']),
    'bg-sunken': tint(base['bg-sunken']),
    accent: primary,
    'accent-on': onColor(primary),
    'accent-text': accentText,
    'accent-secondary': secondary,
    'accent-soft': withAlpha(primary, softAlpha),
  };

  return {
    mode,
    tokens,
    accentScale: scale,
    accentTextFellBack: !primaryOk,
    accentTextFrom: primaryOk ? 'primary' : secondaryOk ? 'secondary' : 'ink',
    swatch: {
      id: theme.accent.kind === 'preset' ? theme.accent.id : `${theme.accent.kind}:${label}`,
      label,
      body: tokens['bg-canvas'],
      surface: tokens['bg-surface'],
      accent: primary,
      mode,
    },
    fontScale: theme.fontScale,
    sounds: theme.sounds,
  };
}

/** CSS custom properties for `<html style>`; the web app stamps these before first paint. */
export function themeToCssVars(resolved: ResolvedTheme): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [k, v] of Object.entries(resolved.tokens)) vars[`--fsp-${k}`] = v;
  resolved.accentScale.forEach((c, i) => {
    vars[`--fsp-accent-${i}`] = c;
  });
  vars['--fsp-font-scale'] = String(resolved.fontScale);
  return vars;
}

/** Swatches for the preset tab of the tile picker. */
export function presetSwatches(): ThemeSwatch[] {
  return THEME_PRESETS.map(
    (p) =>
      resolveTheme(
        { ...DEFAULT_THEME, mode: p.mode, accent: { kind: 'preset', id: p.id } },
        { systemDark: true, lookupTeam: () => undefined },
      ).swatch,
  );
}
